import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  HelpCircle, Book, FileText, Video, MessageCircle, Search,
  ChevronRight, ChevronDown, ExternalLink, Play, Code, Zap,
  Users, Settings, PenTool, Mail, Shield, Keyboard
} from 'lucide-react';

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

interface DocSection {
  id: string;
  title: string;
  icon: React.ElementType;
  description: string;
  articles: Array<{
    title: string;
    content: string;
  }>;
}

export function HelpDocs() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('getting-started');
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);
  const [activeArticle, setActiveArticle] = useState<string | null>(null);

  const categories = [
    { id: 'getting-started', label: 'Getting Started', icon: Zap },
    { id: 'templates', label: 'Templates & DSL', icon: Code },
    { id: 'clients', label: 'Clients & Matters', icon: Users },
    { id: 'documents', label: 'Documents', icon: FileText },
    { id: 'signatures', label: 'E-Signatures', icon: PenTool },
    { id: 'settings', label: 'Settings & Security', icon: Settings },
  ];

  const docSections: DocSection[] = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: Zap,
      description: 'Learn the basics of the document automation platform',
      articles: [
        {
          title: 'Platform Overview',
          content: `
# Platform Overview

Welcome to Lawity's Legal Practice Management Platform. This system streamlines legal document creation through intelligent templates, automated workflows, and seamless client collaboration.

## Key Features

**Template Studio**: Create and manage document templates using our intuitive visual editor and powerful DSL (Domain-Specific Language).

**Client Portal**: Provide clients secure access to their documents, enabling them to review, sign, and download materials.

**Practice Management**: Organize clients and matters with comprehensive tracking and workflow management.

**E-Signature Integration**: Send documents for electronic signature through DocuSign, Dropbox Sign, or our built-in native signing.

## Quick Start

1. **Create a Template**: Navigate to Templates and click "New Template"
2. **Add Variables**: Define dynamic fields using the DSL syntax
3. **Generate Documents**: Fill in variables and generate professional PDFs
4. **Send to Clients**: Share via email or client portal for review/signature
          `,
        },
        {
          title: 'Your First Template',
          content: `
# Creating Your First Template

Follow these steps to create your first document template.

## Step 1: Navigate to Templates

Click on "Templates" in the sidebar to access the Template Studio.

## Step 2: Create New Template

Click the "New Template" button in the top right corner.

## Step 3: Add Basic Information

- **Name**: Give your template a descriptive name
- **Description**: Add a brief description of the template's purpose
- **Category**: Select or create a category for organization

## Step 4: Write Template Content

Use the DSL syntax to create dynamic content:

\`\`\`
DOCUMENT "Simple Agreement"
DESCRIPTION "Basic agreement template"

SECTION "Parties"
  {{party_name}} ("Party")
  Address: {{party_address}}
END

SECTION "Terms"
  This agreement is effective as of {{effective_date | date: 'long'}}.
END
\`\`\`

## Step 5: Preview and Save

Use the Preview panel to see how your document will look with sample data, then click Save.
          `,
        },
        {
          title: 'Dashboard Navigation',
          content: `
# Dashboard Navigation

The dashboard provides an overview of your practice activity and quick access to key features.

## Dashboard Sections

### Stats Cards
View key metrics at a glance:
- Active Clients
- Open Matters
- Documents Generated
- Pending Signatures

### Recent Activity
See the latest document generations, client additions, and signature completions.

### Quick Actions
Access frequently used features:
- Generate Document
- Add Client
- Create Template
- View Reports

## Sidebar Navigation

- **Overview**: Dashboard and Reports
- **Documents**: Templates, Clause Library, All Documents
- **Practice**: Clients and Matters
- **Settings**: Configuration and preferences
          `,
        },
      ],
    },
    {
      id: 'templates',
      title: 'Templates & DSL',
      icon: Code,
      description: 'Master the template DSL and create powerful documents',
      articles: [
        {
          title: 'DSL Syntax Reference',
          content: `
# DSL Syntax Reference

The Domain-Specific Language (DSL) enables you to create dynamic, data-driven documents.

## Basic Structure

\`\`\`
DOCUMENT "Template Name"
DESCRIPTION "Template description"
VERSION "1.0"

SECTION "Section Title"
  Content goes here with {{variables}}
END
\`\`\`

## Variables

Variables are placeholders that get replaced with actual data:

\`\`\`
{{variable_name}}
{{client.name}}
{{property.address}}
\`\`\`

## Filters

Transform variable values with filters:

\`\`\`
{{date | date: 'long'}}           → January 1, 2024
{{amount | currency}}              → $1,234.56
{{name | upper}}                   → JOHN SMITH
{{description | truncate: 100}}    → Truncated text...
\`\`\`

## Conditionals

Show or hide content based on conditions:

\`\`\`
{{#if has_spouse}}
  Spouse: {{spouse_name}}
{{/if}}

{{#if entity_type == "LLC"}}
  Limited Liability Company provisions...
{{else}}
  Corporation provisions...
{{/if}}
\`\`\`

## Lists

Iterate over collections:

\`\`\`
{{#each parcels as parcel}}
  Parcel {{parcel.number}}: {{parcel.description}}
{{/each}}
\`\`\`
          `,
        },
        {
          title: 'Template Best Practices',
          content: `
# Template Best Practices

Follow these guidelines for maintainable, professional templates.

## Naming Conventions

- Use descriptive names: "Wisconsin_Warranty_Deed" not "Template1"
- Include jurisdiction if applicable
- Version your templates when making significant changes

## Variable Organization

Group related variables:

\`\`\`
// Good
{{grantor.name}}
{{grantor.address}}
{{grantee.name}}
{{grantee.address}}

// Avoid
{{name1}}
{{addr1}}
{{name2}}
{{addr2}}
\`\`\`

## Reusable Clauses

Create clauses for frequently used language:

\`\`\`
CLAUSE "standard_warranty_language"
  The Grantor warrants that they are lawfully seized...
END

// Use in templates
{{clause:standard_warranty_language}}
\`\`\`

## Testing

Always preview with various data combinations:
- Test with missing optional fields
- Test with maximum-length values
- Verify date formatting
- Check currency display
          `,
        },
        {
          title: 'Clause Library',
          content: `
# Clause Library

The Clause Library stores reusable text blocks for consistent language across documents.

## Creating Clauses

1. Navigate to Clause Library
2. Click "New Clause"
3. Enter clause details:
   - Name (unique identifier)
   - Category (for organization)
   - Content (the clause text)

## Using Clauses in Templates

Reference clauses using the clause directive:

\`\`\`
{{clause:warranty_language}}
{{clause:signature_block}}
{{clause:governing_law_wisconsin}}
\`\`\`

## Clause Categories

Organize clauses by type:
- **Signature Blocks**: Standard signature sections
- **Warranty Language**: Property warranties
- **Boilerplate**: Standard legal terms
- **Definitions**: Common defined terms

## Versioning

When updating a clause:
1. Consider creating a new version rather than modifying
2. Document changes in the description
3. Test affected templates after updates
          `,
        },
      ],
    },
    {
      id: 'clients',
      title: 'Clients & Matters',
      icon: Users,
      description: 'Manage your client relationships and matters',
      articles: [
        {
          title: 'Managing Clients',
          content: `
# Managing Clients

The Clients section helps you organize and track client information.

## Adding a Client

1. Navigate to Clients
2. Click "Add Client"
3. Select client type:
   - **Individual**: Single person
   - **Entity**: Business, trust, or organization
4. Enter required information
5. Click Save

## Client Information

**Individual Clients:**
- Name (first, middle, last)
- Contact information
- Address
- SSN (for tax documents)

**Entity Clients:**
- Entity name
- Entity type (LLC, Corporation, Trust, etc.)
- EIN
- Principal office address
- Registered agent

## Client Status

- **Active**: Current clients with ongoing matters
- **Inactive**: Former clients (archived)
- **Prospect**: Potential clients

## Linking to Matters

Clients are linked to matters, which contain their documents. Each client can have multiple matters.
          `,
        },
        {
          title: 'Working with Matters',
          content: `
# Working with Matters

Matters organize work for specific client engagements.

## Creating a Matter

1. Navigate to Matters
2. Click "New Matter"
3. Select the client
4. Enter matter details:
   - Name
   - Type (Real Estate, Business Formation, etc.)
   - Description
   - Responsible attorney

## Matter Types

- **Real Estate Purchase (REP)**
- **Real Estate Sale (RES)**
- **Business Formation (BUF)**
- **Estate Planning (ESP)**
- **Trust Administration (TRA)**

## Matter Numbers

Matters receive automatic numbers in the format:
\`YYYY-NNNN-TYP\`

Example: \`2024-0089-REP\`

## Matter Workflow

1. **Open**: Active work
2. **Pending**: Waiting on external action
3. **On Hold**: Temporarily paused
4. **Closed**: Complete
5. **Archived**: Historical record
          `,
        },
      ],
    },
    {
      id: 'signatures',
      title: 'E-Signatures',
      icon: PenTool,
      description: 'Send documents for electronic signature',
      articles: [
        {
          title: 'E-Signature Overview',
          content: `
# E-Signature Overview

The platform supports multiple e-signature providers for legally binding electronic signatures.

## Supported Providers

1. **DocuSign**: Industry-leading e-signature platform
2. **Dropbox Sign**: Simple, affordable signing solution
3. **Native Signing**: Built-in signature capture

## Sending for Signature

1. Open a document
2. Click "Send for Signature"
3. Select signature provider
4. Add signers (name, email, role)
5. Place signature fields
6. Send request

## Signature Workflow

\`\`\`
Sent → Viewed → Partially Signed → Fully Signed → Complete
\`\`\`

## Tracking Signatures

Monitor signature status from:
- Document detail view
- Dashboard pending signatures widget
- Signature status reports
          `,
        },
        {
          title: 'Configuring Signature Providers',
          content: `
# Configuring Signature Providers

Set up e-signature integrations in Settings.

## DocuSign Setup

1. Go to Settings → Integrations
2. Click "Connect" on DocuSign
3. Authorize with your DocuSign account
4. Select default settings

## Dropbox Sign Setup

1. Go to Settings → Integrations
2. Click "Connect" on Dropbox Sign
3. Enter your API key
4. Configure webhook URL

## Native Signing

Built-in signing requires no configuration and includes:
- Draw signature
- Type signature
- Upload signature image

## Provider Selection

When sending documents:
- DocuSign: Best for high-volume, complex signing
- Dropbox Sign: Cost-effective for simple documents
- Native: Quick internal signatures
          `,
        },
      ],
    },
  ];

  const faqs: FAQItem[] = [
    {
      question: 'How do I create a new template?',
      answer: 'Navigate to Templates in the sidebar, click "New Template", and use the DSL editor to create your document structure with variables and conditionals.',
      category: 'templates',
    },
    {
      question: 'Can I import existing Word documents?',
      answer: 'Yes, you can import .docx files. The system will convert formatting and allow you to add variables to create dynamic templates.',
      category: 'templates',
    },
    {
      question: 'How do e-signatures work?',
      answer: 'After generating a document, click "Send for Signature", add signers, and choose your provider (DocuSign, Dropbox Sign, or native). Signers receive an email with a link to sign.',
      category: 'signatures',
    },
    {
      question: 'Can clients access their documents?',
      answer: 'Yes, the Client Portal allows clients to view, download, and sign their documents. Enable portal access when creating or editing a client.',
      category: 'clients',
    },
    {
      question: 'How do I reset my password?',
      answer: 'Go to Settings → Security and click "Change Password". Enter your current password and your new password twice to confirm.',
      category: 'settings',
    },
    {
      question: 'What file formats can I export?',
      answer: 'Documents can be exported as PDF or DOCX. Templates can be exported as JSON for backup or transfer between systems.',
      category: 'documents',
    },
  ];

  const filteredFAQs = faqs.filter(faq =>
    faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const activeSection = docSections.find(s => s.id === activeCategory);

  return (
    <div className="flex-1 overflow-auto bg-slate-900">
      <div className="max-w-6xl mx-auto p-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white mb-2">Help & Documentation</h1>
          <p className="text-slate-400">Find answers, tutorials, and guides for using the platform</p>
        </div>

        {/* Search */}
        <div className="relative mb-8">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
          <input
            type="text"
            placeholder="Search documentation and FAQs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
          />
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <motion.a
            href="#docs"
            whileHover={{ scale: 1.02 }}
            className="p-4 bg-slate-800 border border-slate-700 rounded-xl hover:border-accent-500/50 transition-colors group"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <Book className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h3 className="font-medium text-white group-hover:text-accent-400 transition-colors">Documentation</h3>
                <p className="text-sm text-slate-400">Guides and references</p>
              </div>
            </div>
          </motion.a>

          <motion.a
            href="#faq"
            whileHover={{ scale: 1.02 }}
            className="p-4 bg-slate-800 border border-slate-700 rounded-xl hover:border-accent-500/50 transition-colors group"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                <HelpCircle className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <h3 className="font-medium text-white group-hover:text-accent-400 transition-colors">FAQ</h3>
                <p className="text-sm text-slate-400">Common questions</p>
              </div>
            </div>
          </motion.a>

          <motion.a
            href="#shortcuts"
            whileHover={{ scale: 1.02 }}
            className="p-4 bg-slate-800 border border-slate-700 rounded-xl hover:border-accent-500/50 transition-colors group"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <Keyboard className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <h3 className="font-medium text-white group-hover:text-accent-400 transition-colors">Keyboard Shortcuts</h3>
                <p className="text-sm text-slate-400">Work faster</p>
              </div>
            </div>
          </motion.a>
        </div>

        {/* Documentation Section */}
        <div id="docs" className="mb-12">
          <h2 className="text-lg font-semibold text-white mb-4">Documentation</h2>
          
          <div className="flex gap-6">
            {/* Category Sidebar */}
            <div className="w-64 flex-shrink-0">
              <div className="bg-slate-800 border border-slate-700 rounded-xl p-2">
                {categories.map((cat) => {
                  const Icon = cat.icon;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => {
                        setActiveCategory(cat.id);
                        setActiveArticle(null);
                      }}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                        activeCategory === cat.id
                          ? 'bg-accent-500/20 text-accent-400'
                          : 'text-slate-400 hover:text-white hover:bg-slate-700'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="text-sm font-medium">{cat.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Content Area */}
            <div className="flex-1">
              {activeSection && !activeArticle && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden"
                >
                  <div className="p-6 border-b border-slate-700">
                    <div className="flex items-center gap-3 mb-2">
                      <activeSection.icon className="w-6 h-6 text-accent-400" />
                      <h3 className="text-xl font-semibold text-white">{activeSection.title}</h3>
                    </div>
                    <p className="text-slate-400">{activeSection.description}</p>
                  </div>
                  
                  <div className="divide-y divide-slate-700">
                    {activeSection.articles.map((article, idx) => (
                      <button
                        key={idx}
                        onClick={() => setActiveArticle(article.title)}
                        className="w-full p-4 flex items-center justify-between hover:bg-slate-700/50 transition-colors text-left"
                      >
                        <div className="flex items-center gap-3">
                          <FileText className="w-4 h-4 text-slate-500" />
                          <span className="text-white">{article.title}</span>
                        </div>
                        <ChevronRight className="w-4 h-4 text-slate-500" />
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}

              {activeSection && activeArticle && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden"
                >
                  <div className="p-4 border-b border-slate-700">
                    <button
                      onClick={() => setActiveArticle(null)}
                      className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
                    >
                      <ChevronRight className="w-4 h-4 rotate-180" />
                      <span>Back to {activeSection.title}</span>
                    </button>
                  </div>
                  
                  <div className="p-6 prose prose-invert prose-slate max-w-none">
                    <div 
                      className="text-slate-300"
                      dangerouslySetInnerHTML={{ 
                        __html: activeSection.articles.find(a => a.title === activeArticle)?.content
                          .replace(/^# (.+)$/gm, '<h1 class="text-2xl font-bold text-white mb-4">$1</h1>')
                          .replace(/^## (.+)$/gm, '<h2 class="text-xl font-semibold text-white mt-6 mb-3">$1</h2>')
                          .replace(/^### (.+)$/gm, '<h3 class="text-lg font-medium text-white mt-4 mb-2">$1</h3>')
                          .replace(/\*\*(.+?)\*\*/g, '<strong class="text-white">$1</strong>')
                          .replace(/`([^`]+)`/g, '<code class="px-1.5 py-0.5 bg-slate-700 rounded text-accent-400 text-sm">$1</code>')
                          .replace(/```[\s\S]*?```/g, (match) => {
                            const code = match.slice(3, -3).trim();
                            return `<pre class="bg-slate-900 p-4 rounded-lg overflow-x-auto my-4"><code class="text-sm text-slate-300">${code}</code></pre>`;
                          })
                          .replace(/\n\n/g, '</p><p class="text-slate-300 mb-3">')
                          .replace(/^- (.+)$/gm, '<li class="text-slate-300 ml-4">$1</li>')
                          || ''
                      }}
                    />
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div id="faq" className="mb-12">
          <h2 className="text-lg font-semibold text-white mb-4">Frequently Asked Questions</h2>
          
          <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden divide-y divide-slate-700">
            {filteredFAQs.map((faq, idx) => (
              <div key={idx}>
                <button
                  onClick={() => setExpandedFAQ(expandedFAQ === idx ? null : idx)}
                  className="w-full p-4 flex items-center justify-between text-left hover:bg-slate-700/50 transition-colors"
                >
                  <span className="text-white font-medium">{faq.question}</span>
                  <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform ${
                    expandedFAQ === idx ? 'rotate-180' : ''
                  }`} />
                </button>
                {expandedFAQ === idx && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    className="px-4 pb-4"
                  >
                    <p className="text-slate-400">{faq.answer}</p>
                  </motion.div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Keyboard Shortcuts */}
        <div id="shortcuts" className="mb-12">
          <h2 className="text-lg font-semibold text-white mb-4">Keyboard Shortcuts</h2>
          
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-3">General</h3>
                <div className="space-y-2">
                  {[
                    { keys: ['⌘', 'K'], action: 'Open command palette' },
                    { keys: ['⌘', 'S'], action: 'Save current document' },
                    { keys: ['⌘', 'N'], action: 'New document/template' },
                    { keys: ['⌘', '/'], action: 'Toggle help' },
                  ].map((shortcut, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <span className="text-slate-300">{shortcut.action}</span>
                      <div className="flex gap-1">
                        {shortcut.keys.map((key, kidx) => (
                          <kbd key={kidx} className="px-2 py-1 bg-slate-700 rounded text-xs text-slate-300 font-mono">
                            {key}
                          </kbd>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-3">Editor</h3>
                <div className="space-y-2">
                  {[
                    { keys: ['⌘', 'B'], action: 'Bold text' },
                    { keys: ['⌘', 'I'], action: 'Italic text' },
                    { keys: ['⌘', 'Shift', 'V'], action: 'Insert variable' },
                    { keys: ['⌘', 'P'], action: 'Preview document' },
                  ].map((shortcut, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <span className="text-slate-300">{shortcut.action}</span>
                      <div className="flex gap-1">
                        {shortcut.keys.map((key, kidx) => (
                          <kbd key={kidx} className="px-2 py-1 bg-slate-700 rounded text-xs text-slate-300 font-mono">
                            {key}
                          </kbd>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Support */}
        <div className="bg-gradient-to-r from-accent-500/20 to-primary-500/20 border border-accent-500/30 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-white mb-1">Still need help?</h3>
              <p className="text-slate-400">Our support team is ready to assist you.</p>
            </div>
            <button className="px-4 py-2 bg-accent-500 text-primary-900 rounded-lg font-medium hover:bg-accent-400 transition-colors flex items-center gap-2">
              <MessageCircle className="w-4 h-4" />
              Contact Support
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
